/**
 * Data hook for the Client Success Users view (ADR-0077, Phase 1).
 *
 *   useClientSuccessUsers() — polls GET /api/firm-success/users every 30s.
 *   Read-only; the roster tolerates mild staleness. Bounded payload (~120
 *   users), so a full refetch is cheap. The server returns the table
 *   pre-sorted (Inactive-first, then firm, then name).
 *
 * Spec: engineering/command-center/docs/client-success/phase-1-spec.md (evicted 2026-08-29, #775 round 10)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AttorneyGoodStandingMutationResponseSchema,
  ClientSuccessUserDetailResponseSchema,
  ClientSuccessUsersResponseSchema,
  type AttorneyGoodStandingMutationResponse,
  type ClientSuccessUserDetailResponse,
} from "@command-center/shared";
import { apiDelete, apiGet, apiPost } from "./api";
import { createPollingQuery } from "./create-polling-query";

const POLL_INTERVAL_MS = 30_000;

export const useClientSuccessUsers = createPollingQuery({
  queryKey: ["firm-success", "users"] as const,
  path: "/api/firm-success/users",
  schema: ClientSuccessUsersResponseSchema,
  refetchIntervalMs: POLL_INTERVAL_MS,
});

/**
 * Per-id fetch for the User Detail Panel (Slice I / ADR-0090). Hits
 * `GET /api/firm-success/users/:id` only when `userId` is non-null — the
 * hook is mounted unconditionally on the Users page and short-circuits when
 * no panel is open. Polls at the same cadence as the roster so a freshly
 * created task on the panel reflects without an explicit refetch. Cache key
 * scopes by id so the Dashboard's mutation hooks (Slice I cache-invalidation)
 * can target a single user's payload without blowing the others.
 */
export function useClientSuccessUserDetail(userId: string | null) {
  return useQuery<ClientSuccessUserDetailResponse, Error>({
    queryKey: ["firm-success", "user", userId] as const,
    enabled: userId !== null,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/firm-success/users/${encodeURIComponent(userId!)}`,
      );
      const parsed = ClientSuccessUserDetailResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/firm-success/users/${userId} failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });
}

// ---------------------------------------------------------------------------
// Attorney good-standing document (issue #416)
// ---------------------------------------------------------------------------
// Upload / replace / delete the per-user good-standing certificate. The route
// takes a single JSON+base64 body (no multipart), so the panel reads the File
// to base64 client-side and POSTs JSON. On success the matching user-detail
// cache (`["firm-success","user",<id>]`) is patched directly from the echo —
// `goodStandingFile` + `goodStandingDate` — so the row updates without a
// refetch. Errors propagate as the api.ts taxonomy for the panel to surface.

const USER_DETAIL_KEY = (userId: string) =>
  ["firm-success", "user", userId] as const;

/** Read a File to a base64 string (chunked to stay under the arg-size cap). */
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function applyGoodStanding(
  prev: ClientSuccessUserDetailResponse | undefined,
  result: AttorneyGoodStandingMutationResponse,
): ClientSuccessUserDetailResponse | undefined {
  if (!prev) return prev;
  return {
    user: {
      ...prev.user,
      goodStandingFile: result.file,
      goodStandingDate: result.date,
    },
  };
}

export function useUploadGoodStanding() {
  const queryClient = useQueryClient();
  return useMutation<
    AttorneyGoodStandingMutationResponse,
    Error,
    { userId: string; file: File }
  >({
    mutationFn: async ({ userId, file }) => {
      const contentBase64 = await fileToBase64(file);
      const raw = await apiPost<unknown>(
        `/api/firm-success/users/${encodeURIComponent(userId)}/good-standing`,
        {
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          contentBase64,
        },
      );
      const parsed = AttorneyGoodStandingMutationResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `good-standing upload failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessUserDetailResponse | undefined>(
        USER_DETAIL_KEY(result.id),
        (prev) => applyGoodStanding(prev, result),
      );
    },
  });
}

export function useDeleteGoodStanding() {
  const queryClient = useQueryClient();
  return useMutation<
    AttorneyGoodStandingMutationResponse,
    Error,
    { userId: string }
  >({
    mutationFn: async ({ userId }) => {
      const raw = await apiDelete<unknown>(
        `/api/firm-success/users/${encodeURIComponent(userId)}/good-standing`,
      );
      const parsed = AttorneyGoodStandingMutationResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `good-standing delete failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
    onSuccess: (result) => {
      queryClient.setQueryData<ClientSuccessUserDetailResponse | undefined>(
        USER_DETAIL_KEY(result.id),
        (prev) => applyGoodStanding(prev, result),
      );
    },
  });
}
